import { useEffect, useMemo, useState } from "react";

import { useMediaQuery } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import _ from "lodash";
import { enqueueSnackbar } from "notistack";

import { SkeletonWrapper } from "../../mui-toolkit/SkeletonWrapper";

export default function TilleyFormSelect({
  name,
  value,
  onChange,
  objectName,
  objectNameLabel,
  itemFilter,
  itemSorter,
  itemGetKey,
  itemGetLabel,
  doFetch,
  query, // New parameter: callback function to execute the query

  values,

  isRequired,
  doAllowEmptyElement = false,
  disabled,

  doShowLabel,

  multiple,
  limitTags,

  isLoading,

  autoSelect = true,
  autoHighlight = true,
}) {
  if (isRequired === null) {
    isRequired = true;
  }

  const [selectedValue, setSelectedValue] = useState(value || "");
  const [_values, setValues] = useState(values || []);

  const [isEdited, setIsEdited] = useState(false);

  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("md"), { noSsr: true });

  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar((objectNameLabel || objectName) + ": " + err.detail || err.message || JSON.stringify(err), {
      preventDuplicate: true,
      variant: "error",
    });
  };

  useEffect(() => {
    if (doFetch && query) {
      query(itemFilter)
        .then((selectObjectValues) => {
          let uniqueValues = selectObjectValues;
          if (itemGetKey || itemGetLabel) {
            uniqueValues = uniqueValues.map((currentItem) => {
              return {
                ...currentItem,
                key: itemGetKey ? itemGetKey(currentItem) : currentItem?.key,
                value: itemGetLabel ? itemGetLabel(currentItem) : currentItem?.value,
                uniqueKey:
                  ((itemGetKey && itemGetKey(currentItem)) || "") + "|" + (itemGetLabel && itemGetLabel(currentItem)) ||
                  "",
              };
            });
            uniqueValues = _.uniqBy(uniqueValues, "uniqueKey");
          }

          if (itemSorter && uniqueValues && uniqueValues.length) {
            uniqueValues = uniqueValues.sort(itemSorter);
          } else {
            // Use default sort
            uniqueValues = uniqueValues.sort((a, b) => a.key.localeCompare(b.key));
          }

          setValues(uniqueValues);
        })
        .catch((error) => {
          handleError(error);
        });
    }
  }, [doFetch, query, JSON.stringify(itemFilter)]);

  useEffect(() => {
    setSelectedValue(value || "");
  }, [value]);

  const handleOnBlur = () => {
    setIsEdited(true);
  };

  const handleSelectOnChange = (event) => {
    handleOnBlur();

    setSelectedValue(event?.target?.value || "");

    const newEvent = event;
    newEvent.target.name = name;
    onChange?.(newEvent, event?.target?.value || "");
  };

  const getIsError = useMemo(() => (isRequired ? !value : false), [isRequired, value]);

  const getErrorText = useMemo(() => (getIsError && isEdited ? "Required" : " "), [getIsError, isEdited]);

  return (
    <>
      <FormControl fullWidth className="data-form">
        <SkeletonWrapper variant="textfield" sx={{ marginBottom: "20px" }} isLoading={isLoading}>
          {isSmall && !multiple && !isLoading && (
            <>
              <InputLabel htmlFor={name}>{doShowLabel !== false && (objectNameLabel || objectName)}</InputLabel>
              <Select
                labelId={name}
                value={(selectedValue?.trim && selectedValue?.trim()) || selectedValue || ""}
                multiple={multiple}
                name={name}
                onChange={handleSelectOnChange}
                label={objectNameLabel || objectName}
              >
                {doAllowEmptyElement && <MenuItem key="" value={""}></MenuItem>}
                {_values &&
                  _values?.map((currentItem, index) => {
                    const labelText = itemGetLabel ? itemGetLabel(currentItem) : currentItem?.value;
                    return (
                      <MenuItem key={index} value={labelText?.trim()}>
                        {labelText?.trim()}
                      </MenuItem>
                    );
                  })}
              </Select>
            </>
          )}

          {(!isSmall || multiple) && !isLoading && (
            <Autocomplete
              className="autoComplete"
              disabled={disabled}
              sx={{ width: "100%" }}
              name={name}
              value={(value?.trim && value?.trim()) || value || null}
              multiple={multiple}
              limitTags={limitTags}
              disableCloseOnSelect={multiple && !isSmall}
              options={
                _values &&
                _values?.map((currentItem) => {
                  // Bind primitives:
                  return itemGetLabel ? itemGetLabel(currentItem)?.trim() : currentItem?.value;
                })
              }
              renderInput={(params) => {
                // eslint-disable-next-line no-unused-vars
                const { key, ...inputProps } = params;
                return (
                  <TextField
                    {...inputProps}
                    label={doShowLabel !== false && (objectNameLabel || objectName)}
                    name={name}
                    inputProps={{
                      ...params.inputProps,
                      id: null,
                      m: 0,
                      p: 0,
                    }}
                    sx={{ m: 0, p: 0 }}
                    error={getIsError}
                    helperText={getErrorText}
                  />
                );
              }}
              onChange={(event, value) => {
                const newEvent = event;
                newEvent.target.name = name;
                newEvent.target.value = value || null;
                onChange?.(newEvent, value || "");
              }}
              autoSelect={autoSelect}
              autoHighlight={autoHighlight}
              onBlur={handleOnBlur}
              onFocus={(e) => e.target.select()}
            />
          )}
        </SkeletonWrapper>
      </FormControl>
    </>
  );
}
